
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  watchEffect
} from "vue";
import _ from "lodash";
import { TalkingHead } from "@met4citizen/talkinghead/modules/talkinghead.mjs";
import { HeadAudio } from "@met4citizen/headaudio/modules/headaudio.mjs";
import { useStore } from "vuex";
import {
  RemoteViewConfigDto,
  RequestedAction,
  TelephonyType
} from "@moderrotech/servers-communication-library/src/types";
import Spinner from "@/components/Spinner.vue";
import moment from "moment";
import {
  ResizeObserverEntry,
  useResizeObserver,
  useTimeoutFn,
  useUserMedia
} from "@vueuse/core";
import {
  ClampToEdgeWrapping,
  LinearFilter,
  LinearMipmapLinearFilter,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  MeshStandardMaterial
} from "three";
import imageCompression from "browser-image-compression";
import mime from "mime-types";
import { preloadAvatarGlb } from "@/helpers/avatar";

export default {
  components: { Spinner },

  emits: ["stateChange"],

  setup: function(props: object, context: { emit: Function }) {
    enum PoseName {
      side = "side",
      hip = "hip",
      turn = "turn",
      bend = "bend",
      back = "back",
      straight = "straight",
      wide = "wide",
      oneKnee = "oneknee",
      twoKnees = "kneel",
      sitting = "sitting"
    }
    enum CameraView {
      full = "full",
      upper = "upper",
      head = "head",
      mid = "mid"
    }

    const store = useStore();

    const remoteViewConfig = computed(
        (): RemoteViewConfigDto | null => store.state.session.remoteViewConfig
      ),
      cameraView = computed(
        () =>
          remoteViewConfig.value?.animated_character?.camera_view ??
          CameraView.upper
      );

    const avatarContainer = ref(null),
      ensureIsMounted = () => {
        if (!avatarContainer.value) {
          throw new Error("Component already unmounted");
        }
      };
    let head: any = null,
      headaudio: any = null;

    let micNode: MediaStreamAudioSourceNode | null = null;
    const isLipSyncing = () => !!micNode,
      isSpeaking = ref(false),
      isThinking = ref(false);

    watch(
      () => [isSpeaking.value, isThinking.value],
      ([v1, v2]) => {
        if (!head) {
          return;
        }

        head.isSpeaking = v1; // workaround to force head 'speaking' state

        if (!v1 && !v2) {
          // isThinking.value condition present to avoid canceling 'thinking' animation
          head?.stopSpeaking();
        }
      },
      { immediate: true }
    );

    const isLoading = ref(false);

    const telephonyType = computed(
      (): TelephonyType | null => store.state.telephonyType
    );

    const stream = computed(() =>
      telephonyType.value === TelephonyType.webex
        ? store.state.webex.streams.remoteVideo
        : telephonyType.value === TelephonyType.webrtc
        ? store.state.webrtc.streams.remote
        : null
    );

    // const { stream } = useUserMedia({
    //   enabled: true,
    //   videoDeviceId: false
    // });

    const config = computed(() => store.state.config.config);

    const getFileUrl = (file: string) =>
      `${config.value.publicPath ?? "/"}${file}`;

    const maxInt = Number.MAX_SAFE_INTEGER;

    const targetPose = computed(
        () =>
          isThinking.value
            ? PoseName.straight
            : isSpeaking.value
            ? PoseName.straight
            : _.includes([CameraView.head, CameraView.upper], cameraView.value)
            ? PoseName.straight
            : PoseName.straight //PoseName.side
      ),
      syncPose = async (transition = 1000) => {
        console.log("Syncing avatar pose...", {
          targetPose: targetPose.value,
          transition
        });

        if (head?.poseName === targetPose.value) {
          console.log(`Avatar already in '${targetPose.value}' pose, ignored`);
          return;
        }

        head.poseName = targetPose.value;

        return new Promise((resolve, reject) => {
          if (head?.mixer) {
            head?.mixer.removeEventListener("finished", head?._mixerHandler);
            head?.mixer.stopAllAction();
            head?.mixer.uncacheRoot(head?.armature);
            head.mixer = null;
            head._mixerHandler = null;
          }

          head.animQueue = _.filter(
            head.animQueue,
            x => x.template?.name !== "pose"
          );

          head?.setPoseFromTemplate(
            head?.poseTemplates?.[targetPose.value],
            transition
          );

          window.setTimeout(() => {
            try {
              ensureIsMounted();
              console.log("Avatar pose synced");
              resolve();
            } catch (e) {
              reject();
            }
          }, transition);
        });
      };

    const {
      start: startHeadAudioEndedTimer,
      stop: stopHeadAudioEndedTimer
    } = useTimeoutFn(
      () => {
        if (isSpeaking.value) {
          console.log("Speaking inactive during 1 second, handling");
          isSpeaking.value = false;
          syncPose();
        }
      },
      1000,
      {
        immediate: false
      }
    );

    const startLipSyncing = () => {
        console.log("Starting animated character lip syncing...");

        if (isLipSyncing()) {
          console.log("Animated character lip syncing already started");
          return;
        }

        stopHeadAudioEndedTimer();

        // Resume, if this happens to be the user's first click
        try {
          head?.audioCtx.resume();
        } catch (e) {
          console.error(e);
        }

        micNode = head?.audioCtx.createMediaStreamSource(stream.value!);
        head?.audioSpeechGainNode.disconnect(headaudio);
        micNode?.connect(headaudio);
        headaudio?.resetAll();

        console.log("Animated character lip syncing started");
      },
      stopLipSyncing = () => {
        console.log("Stopping animated character lip syncing...");

        if (!isLipSyncing()) {
          console.log("Animated character lip syncing already stopper");
          return;
        }

        stopHeadAudioEndedTimer();

        if (headaudio) {
          micNode?.disconnect(headaudio);
          head?.audioSpeechGainNode.connect(headaudio);
        }
        micNode = null;
        headaudio?.resetAll();

        isSpeaking.value = false;

        console.log("Animated character lip syncing stopped");
      },
      startThinking = async () => {
        console.log("Starting animated character thinking animation...");

        if (isThinking.value) {
          console.log("Animated character thinking animation already started");
          return;
        }

        stopLipSyncing();

        isThinking.value = true;

        await syncPose(300);

        if (!isThinking.value) {
          console.log(
            "Seems, animated character thinking animation was stopped"
          );
          return;
        }

        const emojiName = "🤔-permanent",
          emoji = _.cloneDeep(head.animEmojis["🤔"]);
        _.set(emoji, "dt.1", maxInt);
        _.set(emoji, "vs.handRight.1.d", maxInt);

        head.animEmojis[emojiName] = emoji;

        //  head?.speakEmoji(emojiName);
        delete head?.animEmojis?.[emojiName];
        head.isSpeaking = false; // workaround to avoid eyes contact and head rotation as during 'speaking' state

        console.log("Animated character thinking animation started");
      },
      stopThinking = () => {
        console.log("Stopping animated character thinking animation...");

        if (!isThinking.value) {
          console.log("Animated character thinking animation already stopped");
          return;
        }

        head?.stopAnimation();

        const emojiName = "🤔-face-release",
          emoji = _.cloneDeep(head.animEmojis["🤔"]);
        _.set(emoji, "dt", [0, 500]);
        _.unset(emoji, "vs.handRight");

        head.animEmojis[emojiName] = emoji;

        //    head?.speakEmoji(emojiName);
        delete head?.animEmojis?.[emojiName];

        isThinking.value = false;
        syncPose();

        console.log("Animated character thinking animation stopped");
      };

    onMounted(async () => {
      const computeView = (container?: Record<"width" | "height", number>) => {
        const isVerticalHead =
          cameraView.value === CameraView.head &&
          container &&
          container.width / container.height < 1;

        return {
          cameraView: cameraView.value,
          modelFPS: 30,
          cameraRotateEnable: false,
          lightAmbientIntensity: 0,
          cameraDistance: isVerticalHead ? 1 : 0,
          cameraY: isVerticalHead ? 0.3 : 0
        };
      };

      const getMeshes = () => {
          const parse = (node: any): Mesh[] =>
            node instanceof Mesh
              ? [node]
              : _.isArray(node?.children)
              ? _.concat([], ..._.map(node?.children, c => parse(c)))
              : [];

          return parse(_.find(head?.scene?.children, "isGroup"));
        },
        findMesh = (name: string) => _.find(getMeshes(), { name }),
        paintMesh = (
          mesh: string | Mesh,
          color: string | [number, number, number]
        ) =>
          _.forEach(
            _.filter(
              _.concat(
                (_.isString(mesh) ? findMesh(mesh) : mesh)?.material ?? []
              ),
              m =>
                m instanceof MeshBasicMaterial ||
                m instanceof MeshStandardMaterial ||
                m instanceof MeshPhongMaterial
                  ? _.isArray(color)
                    ? m?.color?.setRGB(...color)
                    : m?.color?.setHex(
                        parseInt(_.replace(color, "#", "0x"), 16)
                      )
                  : null
            )
          ),
        deleteMesh = (mesh: string | Mesh) => {
          const target = _.isString(mesh) ? findMesh(mesh) : mesh;

          target?.parent?.remove(target);
          target?.geometry.dispose();

          _.isArray(target?.material)
            ? _.forEach(target?.material, m => m.dispose())
            : target?.material?.dispose();
        },
        getTexture = (mesh: string | Mesh) =>
          (_.find(
            _.concat(
              (_.isString(mesh) ? findMesh(mesh) : mesh)?.material ?? []
            ),
            m =>
              (m instanceof MeshBasicMaterial ||
                m instanceof MeshStandardMaterial ||
                m instanceof MeshPhongMaterial) &&
              !!m?.map?.image
          ) as
            | MeshBasicMaterial
            | MeshStandardMaterial
            | MeshPhongMaterial
            | undefined)?.map?.image ?? null,
        changeTexture = (
          mesh: string | Mesh,
          image: string | HTMLImageElement | HTMLCanvasElement
        ) =>
          _.forEach(
            _.concat(
              (_.isString(mesh) ? findMesh(mesh) : mesh)?.material ?? []
            ),
            async m => {
              if (
                !(
                  m instanceof MeshBasicMaterial ||
                  m instanceof MeshStandardMaterial ||
                  m instanceof MeshPhongMaterial
                ) ||
                !m?.map
              ) {
                return;
              }

              m!.depthWrite = true; // Set to true for photos to prevent sorting artifacts
              m!.transparent = true; // Tell Three.js to render the transparent canvas background

              m!.map!.source.data = _.isString(image)
                ? await imageCompression.loadImage(image)
                : image instanceof HTMLCanvasElement
                ? await createImageBitmap(image)
                : image;

              // 1. Fix Photo Artifacts (Use smooth filtering instead of NearestFilter)
              m!.map!.magFilter = LinearFilter;
              m!.map!.minFilter = LinearMipmapLinearFilter; // Smoothes out downscaled high-res photos
              m!.map!.generateMipmaps = true; // Let Three.js build clean downscaled versions

              // 2. Prevent Border Bleeding Artifacts
              m!.map!.wrapS = ClampToEdgeWrapping;
              m!.map!.wrapT = ClampToEdgeWrapping;

              m!.map!.needsUpdate = true;
            }
          );

      isLoading.value = true;

      let logoImg: HTMLImageElement | null = null;

      useResizeObserver(
        avatarContainer.value,
        async (entries: ReadonlyArray<ResizeObserverEntry>) => {
          if (!_.first(entries)) {
            return;
          }

          const view = computeView(entries[0].contentRect);

          if (head) {
            head.setView(view.cameraView, _.omit(view, "cameraView"));
            return;
          }

          head = new TalkingHead(avatarContainer.value, view);

          const avatar = remoteViewConfig.value?.animated_character?.avatar;

          if (!avatar) {
            return;
          }

          const gender =
            "gender" in avatar
              ? avatar?.gender
              : _.first(_.split(avatar?.character, "-")); // male/female

          await Promise.all([
            (async () => {
              await head.audioCtx.audioWorklet.addModule(
                getFileUrl("@met4citizen/headaudio/modules/headworklet.mjs")
              );

              ensureIsMounted();

              headaudio = new HeadAudio(head.audioCtx);

              try {
                await headaudio.loadModel(
                  getFileUrl("@met4citizen/headaudio/dist/model-en-mixed.bin")
                );
              } catch (error) {
                console.error(error);
              }

              ensureIsMounted();

              // Connect speech gain node to lip-sync node + add delay
              head.audioSpeechGainNode.connect(headaudio);
              const delayNode = new DelayNode(head.audioCtx, {
                delayTime: 0.15
              });
              head.audioSpeechGainNode.disconnect(head.audioReverbNode);
              head.audioSpeechGainNode.connect(delayNode);
              delayNode.connect(head.audioReverbNode);

              // Register callback function to set blend shape value
              headaudio.onvalue = (key: string, value: any) => {
                _.assign(head.mtAvatar[key], {
                  newvalue: value,
                  needsUpdate: true
                });
              };

              // Link update method to TalkingHead's animation loop
              head.opt.update = headaudio.update.bind(headaudio);

              // Take eye contact and make a hand gesture when new sentence starts
              let lastEnded = 0;
              headaudio.onended = () => {
                lastEnded = +moment();
                stopHeadAudioEndedTimer();

                if (!isSpeaking.value) {
                  return;
                }

                startHeadAudioEndedTimer();
              };

              headaudio.onstarted = () => {
                stopHeadAudioEndedTimer();

                if (!isSpeaking.value) {
                  isSpeaking.value = true;
                  syncPose();
                }

                const duration = +moment() - lastEnded;
                if (duration > 150) {
                  // New sentence, if 150 ms pause (adjust, if needed)
                  head?.lookAtCamera(500);
                  head?.speakWithHands();
                }
              };
            })(),
            (async () => {
              const url =
                "model" in avatar
                  ? avatar.model
                  : getFileUrl(`avatars/${gender}-merged.glb`);

              try {
                await preloadAvatarGlb(url);
              } catch (err) {
                // Not fatal: showAvatar() falls back to fetching it itself.
                console.error("Model file preloading error: ", {
                  model: url,
                  err
                });
              }

              ensureIsMounted();

              await head.showAvatar(
                {
                  url,
                  body: _.toUpper(_.first(gender)),
                  avatarMood: "neutral",
                  ttsLang: "en-US",
                  ttsVoice: "en-US-Standard-A",
                  avatarSpeakingEyeContact: 1,
                  avatarSpeakingHeadMove: 0
                },
                (ev: any) => {
                  if (ev.lengthComputable) {
                    isLoading.value = ev.loaded < ev.total;
                  }
                }
              );
            })()
          ]);

          ensureIsMounted();

          isLoading.value = false;

          if ("character" in avatar) {
            const meshes = getMeshes(),
              delimiter = "_";

            _.forEach(
              {
                clothes: avatar?.clothes,
                character: avatar?.character,
                hair: avatar?.hair?.style,
                glasses: avatar?.glasses
              },
              (v, k) =>
                _.forEach(
                  _.filter(
                    meshes,
                    (m: Mesh) =>
                      _.startsWith(m.name, `${k}${delimiter}`) &&
                      (_.isNil(v) ||
                        (k === "clothes" &&
                          !avatar?.logo &&
                          !!_.find(
                            ["logo", "badge"],
                            s => m.name === _.join([k, v, s], delimiter)
                          )) ||
                        !(
                          m.name === `${k}${delimiter}${v}` ||
                          _.startsWith(
                            m.name,
                            `${k}${delimiter}${v}${delimiter}`
                          )
                        ))
                  ),
                  (m: Mesh) => deleteMesh(m)
                )
            );

            if (avatar?.clothes && avatar?.logo) {
              (async () => {
                const mesh = findMesh(
                  _.join(["clothes", avatar?.clothes, "logo"], delimiter)
                );

                if (!mesh) {
                  return;
                }

                const texture = getTexture(mesh);

                if (!texture) {
                  return;
                }

                const width = texture?.width ?? 1024,
                  height = texture?.height ?? 1024;

                logoImg =
                  logoImg ??
                  (await imageCompression.loadImage(
                    await imageCompression.getDataUrlFromFile(
                      await (async () => {
                        const blob = await (
                          await fetch(avatar?.logo!, { mode: "cors" })
                        ).blob();

                        return new File(
                          [blob],
                          `file.${mime.extension(blob.type)}`,
                          {
                            type: blob.type,
                            lastModified: +moment()
                          }
                        );
                      })()
                    )
                  ));

                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                canvas.width = width;
                canvas.height = height;
                ctx!.imageSmoothingEnabled = false;
                ctx?.clearRect(0, 0, canvas.width, canvas.height);

                const imgW = logoImg.naturalWidth || logoImg.width,
                  imgH = logoImg.naturalHeight || logoImg.height;

                const padding = findMesh(
                  _.join(["clothes", avatar?.clothes, "badge"], delimiter)
                )
                  ? 0
                  : (_.min([width, height])! * 7) / 100;

                const hRatio = (canvas.width - padding * 2) / imgW,
                  vRatio = (canvas.height - padding * 2) / imgH,
                  ratio = _.min([hRatio, vRatio])!;

                const dx =
                    (canvas.width - padding * 2 - imgW * ratio) / 2 + padding,
                  dy =
                    (canvas.height - padding * 2 - imgH * ratio) / 2 + padding;

                ctx?.drawImage(
                  logoImg,
                  0,
                  0,
                  imgW,
                  imgH,
                  dx,
                  dy,
                  imgW * ratio,
                  imgH * ratio
                );

                changeTexture(mesh, canvas);
              })();
            }

            const hairMesh = (() => {
              if (_.isNil(avatar?.hair?.style)) {
                return null;
              }
              const prefix = `hair${delimiter}${avatar?.hair?.style}`;

              return findMesh(prefix) ?? findMesh(`${prefix}${delimiter}top`);
            })();

            if (hairMesh && avatar?.hair?.color) {
              paintMesh(hairMesh, avatar?.hair?.color);
            }

            const skinColor = _.get(
              {
                "male-3": "#503021",
                "female-3": "#5F3C29"
              },
              avatar?.character ?? "",
              "#F5BDA6"
            ) as string;

            _.forEach(
              _.filter(getMeshes(), m => _.startsWith(m?.name, "hands")),
              m =>
                m?.name ===
                `hands-${
                  gender === "male" &&
                  _.includes(["office-2", "office-3"], avatar?.clothes)
                    ? "avg"
                    : _.find(["casual", "sport"], v =>
                        _.startsWith(avatar?.clothes, v)
                      ) || avatar?.clothes === "office-3"
                    ? "long"
                    : "short"
                }`
                  ? paintMesh(m, skinColor)
                  : deleteMesh(m)
            );
            _.forEach(
              _.filter(getMeshes(), m => _.startsWith(m?.name, "legs")),
              m =>
                m?.name === "legs-short"
                  ? paintMesh(m, skinColor)
                  : deleteMesh(m)
            );
          }

          syncPose();
        }
      );
    });

    onBeforeUnmount(() => stopLipSyncing());

    const requestedRemoteActions = computed(
      (): { action: RequestedAction; payload?: any }[] | null =>
        store.state.session.requestedActions
    );

    watchEffect(() => {
      if (isLoading.value) {
        return;
      }

      if (
        _.findLast(requestedRemoteActions.value, v =>
          _.includes(
            [
              RequestedAction.startThinkingAnimation,
              RequestedAction.stopThinkingAnimation
            ],
            v.action
          )
        )
      ) {
        const isThinkingRequested =
          _.findLast(requestedRemoteActions.value, v =>
            _.includes(
              [
                RequestedAction.startThinkingAnimation,
                RequestedAction.stopThinkingAnimation
              ],
              v.action
            )
          )?.action === RequestedAction.startThinkingAnimation;

        if (isThinkingRequested) {
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.stopThinkingAnimation
          );
          startThinking();
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.startThinkingAnimation
          );
          return;
        } else {
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.startThinkingAnimation
          );
          stopThinking();
          store.commit(
            "session/UNSET_REQUESTED_ACTION",
            RequestedAction.stopThinkingAnimation
          );
        }
      }

      if (
        _.findLast(requestedRemoteActions.value, {
          action: RequestedAction.interactWithAnimatedCharacterAvatar
        })
      ) {
        const payload = _.findLast(requestedRemoteActions.value, {
          action: RequestedAction.interactWithAnimatedCharacterAvatar
        })?.payload as {
          mood:
            | "neutral"
            | "happy"
            | "angry"
            | "sad"
            | "fear"
            | "disgust"
            | "love"
            | "sleep";
          gesture?:
            | "handup"
            | "index"
            | "ok"
            | "thumbup"
            | "thumbdown"
            | "side"
            | "shrug";
          gaze?: "camera" | "away";
        };

        if (payload?.mood) {
          head?.setMood(payload?.mood);
        }

        if (payload?.gesture) {
          head?.playGesture(payload?.gesture, 2.5);
        }

        if (payload?.gaze) {
          if (payload?.gaze === "camera") {
            head?.makeEyeContact?.(2500);
          } else if (payload?.gaze === "away") {
            head?.lookAhead?.(2000);
          }
        }

        store.commit(
          "session/UNSET_REQUESTED_ACTION",
          RequestedAction.interactWithAnimatedCharacterAvatar
        );
      }

      stream.value ? startLipSyncing() : stopLipSyncing();
    });

    watch(
      () =>
        isThinking.value
          ? "thinking"
          : isSpeaking.value
          ? "speaking"
          : isLoading.value
          ? "loading"
          : "listening",
      v => context.emit("stateChange", v),
      { immediate: true }
    );

    return {
      avatarContainer,
      isLoading,
      isThinking,
      config,
      handleToggleThinkingClick: () =>
        store.commit("session/SET_REQUESTED_ACTION", {
          action: isThinking.value
            ? RequestedAction.stopThinkingAnimation
            : RequestedAction.startThinkingAnimation
        })
    };
  }
};
